import {
  IconHome,
  IconClipboardList,
  IconBuildingFactory2,
  IconUsers,
  IconSettings,
  IconChartBar,
  IconTruck,
  IconChartLine,
  IconMap,
  IconCategory,
  IconListDetails,
  IconAlertTriangle,
  IconFileImport,
  IconSend,
} from '@tabler/icons-react';

// Aus AppLayout.tsx gezogen (Plan 35-08): reines Modul, damit der Rollenschnitt automatisiert
// pruefbar ist -- im Projekt gibt es keine Komponententests, aber ueberall reine Module daneben
// (Vorbild: pages/masterdata/kilnActivity.ts). Verhalten unveraendert, nur pruefbar gemacht.
export interface NavItem {
  label: string;
  icon: typeof IconHome;
  path: string;
  badge?: number;
  matchPrefix?: boolean;
}

export const melderNav: NavItem[] = [
  { label: 'Dashboard', icon: IconHome, path: '/dashboard' },
  { label: 'Meldungen', icon: IconClipboardList, path: '/reports', matchPrefix: true },
];

export const trusteeNav: NavItem[] = [
  { label: 'Übersicht', icon: IconChartBar, path: '/evaluations' },
  { label: 'Inlandsversand', icon: IconTruck, path: '/evaluations/inlandsversand' },
  { label: 'Versandstatistik', icon: IconChartLine, path: '/evaluations/versandstatistik' },
  { label: 'Gebietsaufnahme', icon: IconMap, path: '/evaluations/gebietsaufnahme' },
  { label: 'Zementverwendung', icon: IconCategory, path: '/evaluations/zementverwendung' },
  { label: 'Zementarten', icon: IconListDetails, path: '/evaluations/zementarten' },
];

// Phase 35: der neue Import-Punkt haengt allein an isAdmin -- kein eigener bedingter Abschnitt
// wie bei der Unfallauswertung (dort schaltet zusaetzlich eine freigeschaltete Kategorie frei).
// Ein Werksnutzer sieht den Punkt gar nicht, auch nicht lesend (T-35-23).
export const adminNav: NavItem[] = [
  { label: 'Stammdaten', icon: IconBuildingFactory2, path: '/master-data' },
  { label: 'Emissionsberichte', icon: IconFileImport, path: '/emission-reports', matchPrefix: true },
  { label: 'Benutzer', icon: IconUsers, path: '/users' },
  { label: 'Einstellungen', icon: IconSettings, path: '/settings' },
];

// Bereiche mit uebertragbaren Auswertungen laut `StatistikPortalFieldMapping::areas()` (41-03,
// Backend) -- 'environmental' kommt hinzu, sobald Umwelttypen gemappt sind. Das Backend liefert
// die tatsaechliche Bereichsliste je Antwort (403 bei einer nicht lesbaren Auswertung in der URL,
// D-21); diese Konstante spiegelt nur die Navigationsregel, ob der Punkt ueberhaupt erscheint.
export const TRANSFER_READABLE_CATEGORIES = ['market', 'accident'] as const;

export interface HasReadableTransferAreaParams {
  isAdmin: boolean;
  isTrustee: boolean;
  enabledReportCategories: string[];
}

// D-20/D-23: Lesen des Protokolls duerfen Treuhaender ODER Admin, sofern mindestens ein Bereich
// freigeschaltet ist, dessen Auswertungen uebertragen werden koennen -- die Rolle entscheidet nur
// uebers Uebertragen (D-18), nicht ebenfalls uebers Lesen.
export function hasReadableTransferArea({
  isAdmin,
  isTrustee,
  enabledReportCategories,
}: HasReadableTransferAreaParams): boolean {
  if (!isAdmin && !isTrustee) {
    return false;
  }
  return TRANSFER_READABLE_CATEGORIES.some((category) =>
    enabledReportCategories.includes(category)
  );
}

export interface BuildNavSectionsParams {
  isAdmin: boolean;
  isTrustee: boolean;
  hasAccidentCategory: boolean;
  hasReadableTransferArea: boolean;
}

// D-02 (Phase 28): eigene bedingte Sektion fuer die Unfallauswertung statt Eintrag in adminNav --
// adminNav haengt nur an isAdmin und waere sonst fuer jeden Admin sichtbar, auch ohne
// freigeschaltete Kategorie.
export function buildNavSections({
  isAdmin,
  isTrustee,
  hasAccidentCategory,
  hasReadableTransferArea,
}: BuildNavSectionsParams): NavItem[][] {
  const sections: NavItem[][] = [melderNav];
  if (isTrustee) sections.push(trusteeNav);
  if (isAdmin) sections.push(adminNav);
  if (isAdmin && hasAccidentCategory) {
    sections.push([
      { label: 'Unfallauswertung', icon: IconAlertTriangle, path: '/auswertung/unfalldaten' },
    ]);
  }
  // D-23: fuer Treuhaender nach den Auswertungen, fuer Admin nach der Unfallauswertung -- in
  // beiden Faellen der zuletzt angehaengte Abschnitt.
  if (hasReadableTransferArea) {
    sections.push([{ label: 'Übertragungen', icon: IconSend, path: '/transfers' }]);
  }
  return sections;
}
